/**
 * TomTom Morton tile encode + decode (EPSG:4326).
 *
 * This is NOT Web Mercator. It's a simple linear partition of lon/lat:
 *   X: longitude [-180, +180] -> [0, 2^level]
 *   Y: latitude  [-90,  +90]  -> [0, 2^level]
 *
 * Encode pieces (MORTON_LEVEL_DIVISORS, HALF/QUARTER_CIRCLE_UNITS_MD,
 * mortonTileFromCoordinate, interlace, interlaceLong) are ported from
 * com.tomtom.maps.mortontile (MortonTile.fromCoordinate / Interlacer.interlace).
 *
 * Decode (uninterlace + bounds) is the verified inverse, ported from
 * com.tomtom.maps:morton-tile 7.4.
 */

// ---------- Morton Tile encode (ported from com.tomtom.maps.mortontile) ----------

/**
 * MORTON_LEVEL_DIVISORS[i] = 3.6e9 / 2^i
 *
 * Java static initializer:
 *   for (int i = 0; i <= 32; ++i) {
 *       MORTON_LEVEL_DIVISORS[i] = 3.6e9 / (1L << i);
 *   }
 */
const MORTON_LEVEL_DIVISORS = new Float64Array(33);
for (let i = 0; i <= 32; i++) {
  // Java: (double)(1L << i) — use Math.pow to avoid 32-bit overflow at i >= 31
  MORTON_LEVEL_DIVISORS[i] = 3.6e9 / Math.pow(2, i);
}

const HALF_CIRCLE_UNITS_MD = 1800000000; // 180° × 1e7
const QUARTER_CIRCLE_UNITS_MD = 900000000; // 90° × 1e7
const MORTON_CODE_PATTERN = /^M(\d+):([0-9a-f]+)$/i;

/**
 * Port of MortonTile.fromCoordinate(longitude, latitude, level).
 *
 * This is NOT Web Mercator. It's a simple linear partition:
 *   X: longitude  [-180, +180] → [0, 2^level]  (divisor = 3.6e9 / 2^level)
 *   Y: latitude   [-90,  +90]  → [0, 2^level]  (divisor = 3.6e9 / 2^(level+1))
 *
 * Note: Y uses MORTON_LEVEL_DIVISORS[level + 1] because the lat range (180°)
 * is half the lon range (360°), so needs double the divisor to get same tile count.
 *
 * @param {number} lon - Longitude in degrees
 * @param {number} lat - Latitude in degrees
 * @param {number} level - Morton level (23 for anchor IDs)
 * @returns {bigint} Morton code
 */
function mortonTileFromCoordinate(lon, lat, level) {
  level = assertMortonLevel(level);
  const xMd = Math.round(lon * 1e7);
  const yMd = Math.round(lat * 1e7);

  let tileX;
  if (xMd >= HALF_CIRCLE_UNITS_MD) {
    // Boundary case: exactly +180°
    tileX = mortonMax(level);
  } else {
    tileX = Math.trunc(
      (xMd + HALF_CIRCLE_UNITS_MD) / MORTON_LEVEL_DIVISORS[level],
    );
  }

  let tileY;
  if (yMd >= QUARTER_CIRCLE_UNITS_MD) {
    // Boundary case: exactly +90°
    tileY = mortonMax(level);
  } else {
    tileY = Math.trunc(
      (yMd + QUARTER_CIRCLE_UNITS_MD) / MORTON_LEVEL_DIVISORS[level + 1],
    );
  }

  return interlace(tileX, tileY);
}

// ---------- Interlacer (ported from com.tomtom.maps.mortontile.internal.Interlacer) ----------

/**
 * Port of Interlacer.interlace(int x, int y).
 *
 * Java: interlace((long)y << 32 | (long)x)
 * Then applies 5 rounds of swap-and-mask to interleave the bits.
 *
 * All operations use BigInt because JavaScript doesn't have 64-bit integers.
 */
function interlace(x, y) {
  // Pack y into upper 32 bits, x into lower 32 bits
  // Java: interlace(((long) y << 32) + (long) x)
  let raw = (BigInt(y) << 32n) | (BigInt(x) & 0xffffffffn);
  return interlaceLong(raw);
}

/**
 * Port of Interlacer.interlace(long raw).
 *
 * Java swap-and-mask chain:
 *   swapAndMask(raw, 16, 0x00000000FFFF0000L)
 *   swapAndMask(..  , 8,  0x0000FF000000FF00L)
 *   swapAndMask(..  , 4,  0x00F000F000F000F0L)
 *   swapAndMask(..  , 2,  0x0C0C0C0C0C0C0C0CL)
 *   swapAndMask(..  , 1,  0x2222222222222222L)
 *
 * swapAndMask(val, shift, mask):
 *   changes = ((val >>> shift) ^ val) & mask
 *   return val ^ changes + (changes << shift)
 *
 * Note: Java operator precedence: ^ binds lower than +,
 * so "val ^ changes + (changes << shift)" = "val ^ (changes + (changes << shift))"
 */
function interlaceLong(raw) {
  const MASK_64 = 0xffffffffffffffffn;

  function swapAndMask(val, shift, mask) {
    val = val & MASK_64;
    // Java: long changes = (val >>> shift ^ val) & mask;
    // In Java >>> is unsigned right shift. For BigInt, we mask to 64 bits first.
    const shifted = (val >> BigInt(shift)) & MASK_64;
    const changes = (shifted ^ val) & mask;
    // Java: return val ^ changes + (changes << shift);
    // Java precedence: ^ has lower precedence than +
    // So this is: val ^ (changes + (changes << shift))
    const sum = (changes + ((changes << BigInt(shift)) & MASK_64)) & MASK_64;
    return (val ^ sum) & MASK_64;
  }

  let v = raw & MASK_64;
  v = swapAndMask(v, 16, 0x00000000ffff0000n);
  v = swapAndMask(v, 8, 0x0000ff000000ff00n);
  v = swapAndMask(v, 4, 0x00f000f000f000f0n);
  v = swapAndMask(v, 2, 0x0c0c0c0c0c0c0c0cn);
  v = swapAndMask(v, 1, 0x2222222222222222n);
  return v;
}

// ---------- Morton Tile decode (verified inverse, ported from morton-tile 7.4) ----------

const SCALE = 1e7;
const MASK_64 = 0xffffffffffffffffn;

/**
 * One swap-and-mask round, identical to the encode helper but reused for decode.
 *
 * @param {bigint} val - 64-bit value
 * @param {number} shift - Bit shift distance
 * @param {bigint} mask - Bit mask selecting the swap group
 * @returns {bigint} Transformed 64-bit value
 */
function swapAndMask(val, shift, mask) {
  val &= MASK_64;
  const changes = (((val >> BigInt(shift)) & MASK_64) ^ val) & mask;
  const sum = (changes + ((changes << BigInt(shift)) & MASK_64)) & MASK_64;
  return (val ^ sum) & MASK_64;
}

/**
 * Inverse of interlaceLong: de-interleaves the packed Morton code back into
 * tileX (lower 32 bits) and tileY (upper 32 bits).
 *
 * The swap-and-mask shifts ascend 1 -> 16 (the reverse order of interlace).
 *
 * @param {bigint} morton - Morton code
 * @returns {bigint} Packed value where tileY occupies the upper 32 bits
 */
function uninterlace(morton) {
  // shifts ASCEND 1->16 (reverse of interlace)
  let v = BigInt(morton) & MASK_64;
  v = swapAndMask(v, 1, 0x2222222222222222n);
  v = swapAndMask(v, 2, 0x0c0c0c0c0c0c0c0cn);
  v = swapAndMask(v, 4, 0x00f000f000f000f0n);
  v = swapAndMask(v, 8, 0x0000ff000000ff00n);
  v = swapAndMask(v, 16, 0x00000000ffff0000n);
  return v;
}

/**
 * Maximum tile index along one axis at a given level: 2^level - 1.
 *
 * @param {number} level - Morton level
 * @returns {number} Largest valid tile index
 */
function mortonMax(level) {
  return Number(2n ** BigInt(level) - 1n);
}

/**
 * Validate and normalize a TomTom Morton tile level.
 *
 * @param {number} level - Morton level
 * @returns {number} Normalized integer level
 * @throws {Error} If `level` is outside the supported library range
 */
function assertMortonLevel(level) {
  if (!Number.isInteger(level) || level < 0 || level > 31) {
    throw new Error(`Morton level out of range (0-31): ${level}`);
  }
  return level;
}

function maxMortonValueExclusive(level) {
  return 1n << (2n * BigInt(level));
}

function mortonBase4FromValue(morton, level) {
  // Level 0 is the single world tile; its quadkey is the empty string. Without
  // this, padStart(0) leaves "0" (length 1), which mortonBoundsFromBase4 would
  // misread as a level-1 tile and decode to the wrong (origin-corner) box.
  if (level === 0) return "";
  return morton.toString(4).padStart(level, "0");
}

function mortonHexWidth(level) {
  return Math.max(1, Math.ceil(level / 2));
}

/**
 * Parse a canonical TomTom Morton tile code.
 *
 * @param {string} code - Morton tile code in `M<level>:<hex>` format
 * @returns {{level: number, morton: bigint}} Parsed level and Morton code
 * @throws {Error} If `code` is not canonical or is out of range for its level
 */
export function parseMortonCode(code) {
  if (typeof code !== "string") {
    throw new Error(`Invalid Morton tile code: "${code}"`);
  }

  const normalized = code.trim();
  const match = normalized.match(MORTON_CODE_PATTERN);
  if (!match) {
    throw new Error(`Invalid Morton tile code: "${code}"`);
  }

  const level = assertMortonLevel(Number(match[1]));
  const morton = BigInt(`0x${match[2]}`);
  if (morton >= maxMortonValueExclusive(level)) {
    throw new Error(`Morton code out of range for level ${level}: ${match[2]}`);
  }

  return { level, morton };
}

/**
 * Quick syntactic check for whether a token looks like a Morton tile code.
 *
 * @param {string} token - Candidate string
 * @returns {boolean} True when `token` has `M<level>:<hex>` shape
 */
export function looksLikeMortonCode(token) {
  return typeof token === "string" && MORTON_CODE_PATTERN.test(token.trim());
}

/**
 * Check whether a token is a valid Morton tile code.
 *
 * @param {string} token - Candidate string
 * @returns {boolean} True when `token` is a valid `M<level>:<hex>` code
 */
export function isMortonCode(token) {
  try {
    parseMortonCode(token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Encode a coordinate to a canonical TomTom Morton tile code.
 *
 * @param {number} lon - Longitude in degrees (EPSG:4326)
 * @param {number} lat - Latitude in degrees (EPSG:4326)
 * @param {number} level - Morton level
 * @returns {string} Morton tile code in `M<level>:<hex>` format
 */
export function mortonCodeFromCoordinate(lon, lat, level) {
  level = assertMortonLevel(level);
  const morton = mortonTileFromCoordinate(lon, lat, level);
  return `M${level}:${morton.toString(16).padStart(mortonHexWidth(level), "0")}`;
}

/**
 * Convert a canonical Morton tile code to the equivalent base-4 quadkey.
 *
 * @param {string} code - Morton tile code in `M<level>:<hex>` format
 * @returns {string} Base-4 quadkey, left-zero-padded to `level` characters
 */
function mortonBase4FromCode(code) {
  const { level, morton } = parseMortonCode(code);
  return mortonBase4FromValue(morton, level);
}

/**
 * Decode a base-4 Morton quadkey to its geographic bounds (EPSG:4326).
 *
 * Bounds use excludeBorder=true so adjacent tiles do not overlap: the max
 * edges are pulled in by one map unit, except at the +180/+90 world edge
 * where they snap to exactly 180 / 90.
 *
 * @param {string} base4 - Base-4 quadkey ("" means level 0, the whole world)
 * @returns {{minLon: number, minLat: number, maxLon: number, maxLat: number}}
 *   Tile bounds in degrees
 * @throws {Error} If `base4` is not a base-4 string or its level exceeds 31
 */
function mortonBoundsFromBase4(base4) {
  if (!/^[0-3]*$/.test(base4)) {
    throw new Error(`Invalid Morton base-4 quadkey: "${base4}"`);
  }
  const level = assertMortonLevel(base4.length); // "" => level 0

  // base4 string -> morton BigInt
  const morton = base4.split("").reduce((a, c) => a * 4n + BigInt(c), 0n);

  const v = uninterlace(morton);
  const tileX = Number(v & 0xffffffffn);
  const tileY = Number(v >> 32n);
  const max = mortonMax(level);

  const minLon =
    Math.ceil(tileX * MORTON_LEVEL_DIVISORS[level] - HALF_CIRCLE_UNITS_MD) /
    SCALE;
  const minLat =
    Math.ceil(
      tileY * MORTON_LEVEL_DIVISORS[level + 1] - QUARTER_CIRCLE_UNITS_MD,
    ) / SCALE;
  const maxLon =
    tileX === max
      ? 180
      : Math.ceil(
          (tileX + 1) * MORTON_LEVEL_DIVISORS[level] - HALF_CIRCLE_UNITS_MD - 1,
        ) / SCALE;
  const maxLat =
    tileY === max
      ? 90
      : Math.ceil(
          (tileY + 1) * MORTON_LEVEL_DIVISORS[level + 1] -
            QUARTER_CIRCLE_UNITS_MD -
            1,
        ) / SCALE;

  return { minLon, minLat, maxLon, maxLat };
}

/**
 * Decode a canonical Morton tile code to its geographic bounds (EPSG:4326).
 *
 * @param {string} code - Morton tile code in `M<level>:<hex>` format
 * @returns {{minLon: number, minLat: number, maxLon: number, maxLat: number}}
 *   Tile bounds in degrees
 */
export function mortonBoundsFromCode(code) {
  return mortonBoundsFromBase4(mortonBase4FromCode(code));
}

/**
 * Decode a base-4 Morton quadkey to a closed lon/lat ring suitable for a polygon.
 *
 * @param {string} base4 - Base-4 quadkey
 * @returns {number[][]} Closed ring:
 *   [[minLon,minLat],[maxLon,minLat],[maxLon,maxLat],[minLon,maxLat],[minLon,minLat]]
 */
function mortonRingFromBase4(base4) {
  const { minLon, minLat, maxLon, maxLat } = mortonBoundsFromBase4(base4);
  return [
    [minLon, minLat],
    [maxLon, minLat],
    [maxLon, maxLat],
    [minLon, maxLat],
    [minLon, minLat],
  ];
}

/**
 * Decode a canonical Morton tile code to a closed lon/lat ring.
 *
 * @param {string} code - Morton tile code in `M<level>:<hex>` format
 * @returns {number[][]} Closed ring
 */
export function mortonRingFromCode(code) {
  return mortonRingFromBase4(mortonBase4FromCode(code));
}

/**
 * Decode a canonical Morton tile code to a WKT POLYGON string in EPSG:4326.
 *
 * @param {string} code - Morton tile code in `M<level>:<hex>` format
 * @returns {string} WKT POLYGON
 */
export function mortonWktFromCode(code) {
  const ring = mortonRingFromCode(code)
    .map(([lon, lat]) => `${lon} ${lat}`)
    .join(", ");
  return `POLYGON ((${ring}))`;
}
